import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell, Check, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import notificationService from '@/services/notificationService.ts';
import type { NotificationResponse } from '@/types/notification.ts';

function timeAgo(dateStr: string, t: (key: string, options?: Record<string, unknown>) => string): string {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diffSeconds = Math.floor((now - date) / 1000);

  if (diffSeconds < 60) return t('notifications.timeJustNow');
  const diffMinutes = Math.floor(diffSeconds / 60);
  if (diffMinutes < 60) return t('notifications.timeMinutes', { count: diffMinutes });
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return t('notifications.timeHours', { count: diffHours });
  const diffDays = Math.floor(diffHours / 24);
  return t('notifications.timeDays', { count: diffDays });
}

export default function NotificationBell() {
  const { t } = useTranslation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState<NotificationResponse[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const fetchUnreadCount = useCallback(async () => {
    try {
      const data = await notificationService.getUnreadCount();
      setUnreadCount(data.count);
    } catch {
      // silent fail for polling
    }
  }, []);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 10_000);
    // Refetch al volver a la pestana / ventana — cubre el caso "creo sim en
    // otra tab, vuelvo aqui, espero ver el badge fresco sin esperar al tick".
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchUnreadCount();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', fetchUnreadCount);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', fetchUnreadCount);
    };
  }, [fetchUnreadCount]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const handleToggle = async () => {
    const willOpen = !open;
    setOpen(willOpen);
    if (willOpen) {
      setLoading(true);
      try {
        const data = await notificationService.getAll();
        setNotifications(data);
        // Sync badge con la lista recien cargada — cubre el caso en que el
        // backend ha purgado notif (ej. sim borrada) entre dos ticks del poll.
        setUnreadCount(data.filter((n) => !n.isRead).length);
      } catch {
        // silent fail
      } finally {
        setLoading(false);
      }
    }
  };

  const handleNotificationClick = async (notification: NotificationResponse) => {
    if (!notification.isRead) {
      try {
        await notificationService.markAsRead(notification.id);
        setNotifications((prev) =>
          prev.map((n) => (n.id === notification.id ? { ...n, isRead: true } : n)),
        );
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch {
        // silent fail
      }
    }
    if (notification.relatedEntityId) {
      setOpen(false);
      if (notification.type === 'report_ready' || notification.type === 'report_failed') {
        navigate('/progress', { state: { openSessionId: notification.relatedEntityId } });
      } else {
        navigate(`/simulation/${notification.relatedEntityId}`);
      }
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch {
      // silent fail
    }
  };

  const hasReadNotifications = notifications.some((n) => n.isRead);

  const handleDeleteRead = async () => {
    try {
      await notificationService.deleteRead();
      setNotifications((prev) => prev.filter((n) => !n.isRead));
    } catch {
      // silent fail
    }
  };

  return (
    <div ref={ref} className="relative">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={handleToggle}
        className="relative"
        aria-label={t('notifications.ariaLabel')}
      >
        <Bell className="size-[18px]" />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-white">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full z-40 mt-2 w-96 max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-card shadow-lg animate-fade-in">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">{t('notifications.title')}</h3>
            <div className="flex flex-wrap items-center gap-1">
              {unreadCount > 0 && (
                <Button variant="ghost" size="xs" onClick={handleMarkAllAsRead} className="gap-1 text-primary">
                  <Check className="size-3" />
                  {t('notifications.markAllRead')}
                </Button>
              )}
              {hasReadNotifications && (
                <Button variant="ghost" size="xs" onClick={handleDeleteRead} className="gap-1 text-muted-foreground hover:text-destructive">
                  <Trash2 className="size-3" />
                  {t('notifications.clearRead')}
                </Button>
              )}
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                {t('notifications.loading')}
              </div>
            ) : notifications.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">
                {t('notifications.empty')}
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted ${
                    !notification.isRead ? 'bg-primary/5' : ''
                  }`}
                >
                  {!notification.isRead && (
                    <span className="mt-1.5 size-2 shrink-0 rounded-full bg-primary" />
                  )}
                  <div className={`flex-1 ${notification.isRead ? 'pl-5' : ''}`}>
                    <p className="text-sm font-medium text-foreground">{notification.title}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">{notification.message}</p>
                    <p className="mt-1 text-xs text-muted-foreground/70">{timeAgo(notification.createdAt, t)}</p>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
